import { useState, type FormEvent } from "react";
import { ArrowRight, Check, Mail, Sparkles } from "lucide-react";
import { motion } from "motion/react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EarlyAccessHero() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");

    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) {
      setError("Enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/early-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: clean, source: "king-homepage" }),
      });
      if (!res.ok) throw new Error("bad response");
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="relative overflow-hidden bg-[#07070a] text-[#f5f1ea] border-b border-white/5">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(200,16,46,0.24),transparent_35%),radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.08),transparent_22%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:72px_72px] opacity-25" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-8 py-14 md:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.08fr_0.92fr] items-center">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-[11px] uppercase tracking-[0.28em] text-white/70">
              <span className="w-2 h-2 rounded-full bg-[#c8102e] animate-pulse" />
              Early Access
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="mt-6 text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.95]"
            >
              Build your own
              <span className="block text-[#c8102e] font-serif italic font-normal mt-3">
                AI operating system.
              </span>
            </motion.h1>

            <p className="mt-6 max-w-2xl text-base md:text-lg leading-8 text-white/68">
              Drop your email and get the link to Sloe Laboratory. The Architect shapes your OS, your agents, and your data around the business you actually run.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="mt-8 max-w-xl">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <label className="sr-only" htmlFor="early-access-email">
                    Email address
                  </label>
                  <div className="relative flex-1">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      id="early-access-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-11 py-4 text-[15px] text-white outline-none placeholder:text-white/30 focus:border-[#c8102e]/70 focus:bg-white/[0.08] transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#c8102e] px-6 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#f5f1ea] transition-transform hover:-translate-y-0.5 hover:bg-[#e01234] disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {loading ? "Sending..." : "Send me the link"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                {error && <p className="mt-3 text-sm text-[#ff8c9d]">{error}</p>}
                <p className="mt-4 text-[12px] uppercase tracking-[0.16em] text-white/35">
                  No spam. One email with your access link.
                </p>
              </form>
            ) : (
              <div className="mt-8 max-w-xl rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-400/10">
                    <Check className="w-5 h-5 text-emerald-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-100">Check your inbox.</p>
                    <p className="text-sm text-emerald-100/70">
                      Your access link is on its way. Open it on a laptop or desktop.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#examples"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:bg-white/[0.08] transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#c8102e]" />
                View live systems
              </a>
              <span className="text-xs uppercase tracking-[0.18em] text-white/35">
                Best experienced on a laptop or desktop
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -z-10 rounded-[2rem] bg-[#c8102e]/10 blur-3xl" />
            <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-2xl shadow-black/40 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.28em] text-white/35">Sloe Laboratory</p>
                  <p className="mt-1 text-lg font-semibold text-[#f5f1ea]">The Experiment</p>
                </div>
                <div className="rounded-full border border-[#c8102e]/30 bg-[#c8102e]/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#ff9ca8]">
                  Live
                </div>
              </div>

              <div className="p-6 md:p-8 space-y-4">
                <div className="rounded-2xl border border-white/[0.08] bg-black/30 p-5">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">What you get</p>
                  <h2 className="mt-3 text-2xl md:text-3xl font-semibold leading-tight">
                    A founder-facing OS that looks built, not borrowed.
                  </h2>
                  <p className="mt-3 text-sm md:text-base leading-7 text-white/65">
                    The homepage becomes the door, the product remains visible underneath, and every new lead flows straight into your existing pipeline.
                  </p>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  {[
                    ["Inbound", "Forward `kingkay@sloelabs.com` to Gmail"],
                    ["Outbound", "Send as founder when your sender is ready"],
                    ["Product", "Show the live OS instead of a dead external link"],
                  ].map(([title, body]) => (
                    <div key={title} className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-white/35">{title}</p>
                      <p className="mt-2 text-sm leading-6 text-white/70">{body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
